import { useState, useMemo, useRef, useEffect, useCallback } from "react"
import * as XLSX from "xlsx"
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LabelList,
  PieChart, Pie, Cell, ResponsiveContainer
} from "recharts"

const T = {
  textPrimary:"#0f172a", textSecondary:"#475569", textTertiary:"#94a3b8",
  bgPrimary:"#ffffff", bgSecondary:"#f8fafc", bgTertiary:"#f1f5f9",
  borderTertiary:"#e2e8f0", borderSecondary:"#cbd5e1",
  radiusMd:"8px", radiusLg:"12px",
  font:"'Inter', -apple-system, system-ui, sans-serif",
  shadow:"0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.06)",
  shadowLg:"0 10px 25px rgba(0,0,0,0.08)",
}

const PALETTE = ["#F3B51A", "#E24078", "#F67A23", "#119193", "#613CCA", "#176EE0"]

const hslToHex = (h, s, l) => {
  s /= 100
  l /= 100
  const k = n => (n + h / 30) % 12
  const a = s * Math.min(l, 1 - l)
  const f = n => {
    const c = l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))
    return Math.round(255 * c).toString(16).padStart(2, "0")
  }
  return `#${f(0)}${f(8)}${f(4)}`
}

const getColor = (index) => {
  if (index < PALETTE.length) return PALETTE[index]
  const overflowIdx = index - PALETTE.length
  const hue = (35 + overflowIdx * 137.508) % 360
  const saturation = 62 + (overflowIdx % 3) * 8
  const lightness = 48 + (overflowIdx % 2) * 8
  return hslToHex(hue, saturation, lightness)
}

const useViewport = () => {
  const getCategory = (w) => {
    if (w < 640) return "mobile"
    if (w < 1024) return "tablet"
    if (w < 1440) return "laptop"
    return "desktop"
  }

  const [vp, setVp] = useState(() =>
    typeof window !== "undefined" ? getCategory(window.innerWidth) : "desktop"
  )

  useEffect(() => {
    const onResize = () => setVp(getCategory(window.innerWidth))
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  return vp
}

const buildColorMap = (keys) =>
  Object.fromEntries(keys.map((k, i) => [k, getColor(i)]))

const uniq = (arr) => [...new Set(arr)]
const sorted = (arr) => [...arr].sort()

const agg = (rows, keys) => {
  const map = {}

  rows.forEach(r => {
    const k = keys.map(g => r[g]).join("||")

    if (!map[k]) {
      map[k] = {
        ...Object.fromEntries(keys.map(g => [g, r[g]])),
        Volume: 0,
      }
    }

    map[k].Volume += r.Volume
  })

  return Object.values(map)
}

const topNMakers = (rows, n = 5) => {
  const totals = {}

  rows.forEach(r => {
    totals[r.Maker] = (totals[r.Maker] || 0) + r.Volume
  })

  return Object.entries(totals)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(e => e[0])
}

const WEIGHT_RANGES = ["12.1-15 t", "15.1-20 t", "20.1-25 t", "25.1-30 t", ">30 t"]

const isWeightRange = (sub) =>
  sub && WEIGHT_RANGES.includes(String(sub).trim())

const MultiSelect = ({ label, options, value, onChange }) => {
  const [open, setOpen] = useState(false)

  const allSelected = options.length > 0 && value.length === options.length
  const noneSelected = value.length === 0
  const summary = noneSelected
    ? "None"
    : allSelected
    ? "All"
    : `${value.length} / ${options.length}`

  return (
    <div style={{ marginBottom: 10, position: "relative" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width:"100%",
          display:"flex",
          alignItems:"center",
          justifyContent:"space-between",
          padding:"5px 8px",
          fontSize:"11px",
          fontWeight:"600",
          cursor:"pointer",
          borderRadius: T.radiusMd,
          border:`1px solid ${T.borderSecondary}`,
          background: open ? T.bgTertiary : T.bgSecondary,
          color: T.textPrimary,
        }}
      >
        <span
          style={{
            color:T.textTertiary,
            textTransform:"uppercase",
            letterSpacing:"0.07em",
            fontSize:"10px",
          }}
        >
          {label}
        </span>

        <span style={{ display:"flex", alignItems:"center", gap:4 }}>
          <span
            style={{
              fontSize:"10px",
              color:T.textSecondary,
              fontWeight:"500",
            }}
          >
            {summary}
          </span>
          <span style={{ fontSize:9, color:T.textTertiary }}>
            {open ? "▲" : "▼"}
          </span>
        </span>
      </button>

      {open && (
        <div
          style={{
            position:"absolute",
            top:"100%",
            left:0,
            right:0,
            zIndex:999,
            background:T.bgPrimary,
            border:`1px solid ${T.borderSecondary}`,
            borderRadius:T.radiusMd,
            boxShadow:T.shadowLg,
            marginTop:2,
            overflow:"hidden",
          }}
        >
          <div
            style={{
              display:"flex",
              borderBottom:`1px solid ${T.borderTertiary}`,
            }}
          >
            <button
              onClick={() => onChange([...options])}
              style={{
                flex:1,
                fontSize:"10px",
                padding:"5px",
                cursor:"pointer",
                border:"none",
                background: allSelected ? "#dbeafe" : T.bgSecondary,
                color:"#3b82f6",
                fontWeight:"600",
                borderRight:`1px solid ${T.borderTertiary}`,
              }}
            >
              All
            </button>

            <button
              onClick={() => onChange([])}
              style={{
                flex:1,
                fontSize:"10px",
                padding:"5px",
                cursor:"pointer",
                border:"none",
                background: noneSelected ? "#fee2e2" : T.bgSecondary,
                color:"#ef4444",
                fontWeight:"600",
              }}
            >
              None
            </button>
          </div>

          <div style={{ maxHeight:160, overflowY:"auto", padding:"4px 0" }}>
            {options.length === 0 && (
              <div
                style={{
                  padding:"8px 10px",
                  fontSize:"10px",
                  color:T.textTertiary,
                  fontStyle:"italic",
                  textAlign:"center",
                }}
              >
                No options for selected product(s)
              </div>
            )}
            {options.map(opt => (
              <label
                key={opt}
                style={{
                  display:"flex",
                  alignItems:"center",
                  gap:8,
                  padding:"5px 10px",
                  fontSize:"11px",
                  cursor:"pointer",
                  color:T.textPrimary,
                  background: value.includes(opt) ? "#eff6ff" : "transparent",
                  userSelect:"none",
                }}
              >
                <input
                  type="checkbox"
                  checked={value.includes(opt)}
                  onChange={() =>
                    onChange(
                      value.includes(opt)
                        ? value.filter(v => v !== opt)
                        : [...value, opt]
                    )
                  }
                  style={{
                    accentColor:"#3b82f6",
                    width:12,
                    height:12,
                    cursor:"pointer",
                  }}
                />
                {opt}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const MakerSelect = ({ label, value, onChange, makers }) => {
  const [open, setOpen] = useState(false)

  return (
    <div style={{ marginBottom: 10, position: "relative" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width:"100%",
          display:"flex",
          alignItems:"center",
          justifyContent:"space-between",
          padding:"5px 8px",
          fontSize:"11px",
          fontWeight:"600",
          cursor:"pointer",
          borderRadius: T.radiusMd,
          border:`1px solid ${T.borderSecondary}`,
          background: open ? T.bgTertiary : T.bgSecondary,
          color: T.textPrimary,
        }}
      >
        <span
          style={{
            color:T.textTertiary,
            textTransform:"uppercase",
            letterSpacing:"0.07em",
            fontSize:"10px",
          }}
        >
          {label}
        </span>

        <span style={{ display:"flex", alignItems:"center", gap:4 }}>
          <span
            style={{
              fontSize:"10px",
              color:T.textSecondary,
              fontWeight:"500",
            }}
          >
            {value}
          </span>
          <span style={{ fontSize:9, color:T.textTertiary }}>
            {open ? "▲" : "▼"}
          </span>
        </span>
      </button>

      {open && (
        <div
          style={{
            position:"absolute",
            top:"100%",
            left:0,
            right:0,
            zIndex:999,
            background:T.bgPrimary,
            border:`1px solid ${T.borderSecondary}`,
            borderRadius:T.radiusMd,
            boxShadow:T.shadowLg,
            marginTop:2,
            overflow:"hidden",
          }}
        >
          <div style={{ maxHeight:160, overflowY:"auto", padding:"4px 0" }}>
            {makers.map(m => (
              <div
                key={m}
                onClick={() => {
                  onChange(m)
                  setOpen(false)
                }}
                style={{
                  padding:"6px 10px",
                  fontSize:"11px",
                  cursor:"pointer",
                  background: m === value ? "#eff6ff" : "transparent",
                  color: m === value ? "#3b82f6" : T.textPrimary,
                  fontWeight: m === value ? "600" : "400",
                }}
              >
                {m}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const ChartLegend = ({ items, colorMap, title = "Legend" }) => (
  <div
    style={{
      marginTop:14,
      padding:"10px 12px",
      background:T.bgSecondary,
      borderRadius:T.radiusMd,
      border:`1px solid ${T.borderTertiary}`,
    }}
  >
    <div
      style={{
        fontSize:"10px",
        fontWeight:"600",
        color:T.textTertiary,
        textTransform:"uppercase",
        letterSpacing:"0.07em",
        marginBottom:8,
      }}
    >
      {title}
    </div>

    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {items.map(item => (
        <span
          key={item}
          style={{
            display:"flex",
            alignItems:"center",
            gap:7,
            fontSize:"11px",
            color:T.textPrimary,
            fontWeight:"500",
            whiteSpace:"nowrap",
            overflow:"hidden",
            textOverflow:"ellipsis",
          }}
        >
          <span
            style={{
              width:11,
              height:11,
              borderRadius:3,
              background: colorMap[item] || "#aaa",
              display:"inline-block",
              boxShadow:"0 1px 2px rgba(0,0,0,0.08)",
              flexShrink:0,
            }}
          />
          <span style={{ overflow:"hidden", textOverflow:"ellipsis" }}>
            {item}
          </span>
        </span>
      ))}
    </div>
  </div>
)

const EmptyState = () => (
  <div style={{ padding:24, textAlign:"center", color:T.textSecondary, fontSize:12 }}>
    No data — adjust filters.
  </div>
)

// ─── Single facet panel — measures actual bar paths in DOM for the cumulative-total label ───
const FacetPanel = ({ fv, fvIdx, chartData, stacks, xKey, facetKey, colorMap, h, yMax }) => {
  const ref = useRef(null)
  const [barCenters, setBarCenters] = useState([])

  const isFirst = fvIdx === 0
  const isRotated = xKey === "Maker"
  const lastSk = stacks[stacks.length - 1]
  // First panel needs room for y-axis labels; rest can hug closer
  const leftMargin = isFirst ? 4 : -28

  const measureBars = useCallback(() => {
    const el = ref.current
    if (!el) return false

    const allBars = el.querySelectorAll(".recharts-bar")
    if (!allBars.length) return false

    const containerRect = el.getBoundingClientRect()

    // Collect every stack's rect array. Then walk by data-index (i):
    //   • x:    first stack with a non-zero-width rect at this index
    //   • topY: smallest top-Y among visible (height > 0.5) rects at this index
    // This handles bars whose bottom stack is 0 (those rects exist but have
    // height ≈ 0; their width / x is still valid) and bars where some upper
    // stacks are skipped entirely.
    const allBarRects = Array.from(allBars).map(bar =>
      Array.from(
        bar.querySelectorAll(
          ".recharts-bar-rectangle path, .recharts-bar-rectangle rect"
        )
      )
    )

    const maxLen = allBarRects.reduce((m, arr) => Math.max(m, arr.length), 0)
    if (!maxLen) return false

    const positions = []
    for (let i = 0; i < maxLen; i++) {
      let x = null
      let topY = Infinity
      let foundVisible = false

      for (let bi = 0; bi < allBarRects.length; bi++) {
        const path = allBarRects[bi][i]
        if (!path) continue
        const bbox = path.getBoundingClientRect()

        if (bbox.width > 0.5) {
          if (x === null) {
            x = bbox.left - containerRect.left + bbox.width / 2
          }
          if (bbox.height > 0.5) {
            const t = bbox.top - containerRect.top
            if (t < topY) topY = t
            foundVisible = true
          }
        }
      }

      positions.push(x !== null && foundVisible ? { x, topY } : null)
    }

    setBarCenters(positions)
    return true
  }, [chartData])

  useEffect(() => {
    let cancelled = false
    const timers = []

    const tries = [50, 150, 400, 800]
    tries.forEach(delay => {
      const t = setTimeout(() => {
        if (!cancelled) measureBars()
      }, delay)
      timers.push(t)
    })

    const el = ref.current
    let ro = null

    if (el && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => measureBars())
      ro.observe(el)
    }

    return () => {
      cancelled = true
      timers.forEach(clearTimeout)
      if (ro) ro.disconnect()
    }
  }, [measureBars, chartData, h])

  // Format y-axis numbers (e.g. 1500 → "1.5k", 12000 → "12k")
  const fmtY = (v) => {
    if (v >= 1000) return `${(v / 1000).toFixed(v < 10000 ? 1 : 0)}k`
    return v
  }

  return (
    <div
      ref={ref}
      style={{
        flex:"1 1 0",
        minWidth: isRotated ? 180 : 140,
        paddingTop: 36,
        position: "relative",
      }}
    >
      {barCenters.length > 0 && chartData.map((d, i) => {
        const cum = d[`${lastSk}_cum`]
        const pos = barCenters[i]
        if (!cum || !pos) return null

        // Sit the label ~22px above the bar's top (more breathing room),
        // clamped so it never escapes the container's top edge.
        const labelTop = Math.max(2, pos.topY - 22)

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              top: labelTop,
              left: pos.x,
              transform: "translateX(-50%)",
              fontSize: "10px",
              fontWeight: "700",
              color: T.textPrimary,
              whiteSpace: "nowrap",
              pointerEvents: "none",
              zIndex: 10,
            }}
          >
            {cum.toLocaleString()}
          </div>
        )
      })}

      <ResponsiveContainer width="100%" height={h}>
        <BarChart
          data={chartData}
          barSize={56}
          barCategoryGap="20%"
          margin={{
            top:20,
            right:4,
            left: leftMargin,
            bottom: isRotated ? 55 : 28,
          }}
        >
          <CartesianGrid strokeDasharray="2 3" stroke={T.borderTertiary} vertical={false} />

          <XAxis
            dataKey={xKey}
            tick={{
              fontSize: facetKey !== "CY" ? 10 : 8.5,
              fontWeight: facetKey !== "CY" ? "700" : "400",
              fill: T.textPrimary,
            }}
            angle={isRotated ? -38 : 0}
            textAnchor={isRotated ? "end" : "middle"}
            interval={0}
            label={
              facetKey === "CY"
                ? {
                    value: fv,
                    position: "insideBottom",
                    offset: isRotated ? -48 : -16,
                    fontSize: 11,
                    fontWeight: "700",
                    fill: T.textPrimary,
                  }
                : undefined
            }
          />

          <YAxis
            tick={isFirst ? { fontSize:9 } : false}
            axisLine={isFirst}
            tickLine={isFirst}
            domain={[0, yMax]}
            tickFormatter={fmtY}
          />

          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload) return null

              const filtered = payload.filter(entry => entry.value > 0)
              if (!filtered.length) return null

              return (
                <div
                  style={{
                    background:"#fff",
                    border:"1px solid #ccc",
                    borderRadius:4,
                    padding:"12px",
                    fontSize:11,
                    boxShadow:"0 2px 8px rgba(0,0,0,0.15)",
                  }}
                >
                  <p
                    style={{
                      margin:0,
                      fontWeight:600,
                      marginBottom:8,
                      color:T.textPrimary,
                    }}
                  >
                    {label}
                  </p>

                  {filtered.map((entry, i) => {
                    const absVal = entry.payload[`${entry.name}_abs`] || 0
                    const pct = Math.round(entry.payload[`${entry.name}_pct`] || 0)

                    return (
                      <p key={i} style={{ margin:"4px 0", color:entry.color }}>
                        <strong>{entry.name}:</strong> {absVal.toLocaleString()} ({pct}%)
                      </p>
                    )
                  })}
                </div>
              )
            }}
          />

          {stacks.map((sk, idx) => {
            const isTop = idx === stacks.length - 1
            return (
              <Bar
                key={sk}
                dataKey={sk}
                stackId="s"
                fill={colorMap[sk] || "#aaa"}
                isAnimationActive={false}
                radius={isTop ? [6, 6, 0, 0] : 0}
              >
                <LabelList
                  dataKey={sk}
                  content={(props) => {
                    const { x, y, width, height, value, index } = props
                    // value is now the absolute volume; pct comes from chart data
                    const pct = chartData[index]?.[`${sk}_pct`] || 0

                    if (!value || pct < 5) return null

                    const absVal = value
                    const cx = x + width / 2
                    const cy = y + height / 2
                    // Only show both labels if the segment is tall enough
                    const showBoth = height >= 26

                    return (
                      <g>
                        <text
                          x={cx}
                          y={showBoth ? cy - 5 : cy}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fill="#fff"
                          fontSize={9}
                          fontWeight="700"
                          style={{ pointerEvents:"none" }}
                        >
                          {absVal.toLocaleString()}
                        </text>

                        {showBoth && (
                          <text
                            x={cx}
                            y={cy + 7}
                            textAnchor="middle"
                            dominantBaseline="central"
                            fill="#fff"
                            fontSize={8}
                            fontWeight="500"
                            opacity={0.9}
                            style={{ pointerEvents:"none" }}
                          >
                            ({Math.round(pct)}%)
                          </text>
                        )}
                      </g>
                    )
                  }}
                />
              </Bar>
            )
          })}
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

const FacetedBar = ({ data, xKey, stackKey, colorMap, facetKey, facetValues, h = 320 }) => {
  const stacks = sorted(uniq(data.map(d => d[stackKey])))

  if (!facetValues.length || !data.length) return <EmptyState />

  // Pre-compute every facet's chart data so we can find the global max
  // and share one y-axis scale across all facets (essential for trend
  // comparison when using non-100% stacks).
  let maxTotal = 0

  const allFacetData = facetValues.map(fv => {
    const fRows = data.filter(d => d[facetKey] === fv)
    const xVals = sorted(uniq(fRows.map(d => d[xKey])))

    const chartData = xVals.map(xv => {
      const stackData = {}
      let total = 0

      stacks.forEach(sk => {
        const val = fRows
          .filter(d => d[xKey] === xv && d[stackKey] === sk)
          .reduce((s, r) => s + r.Volume, 0)

        stackData[sk] = val // ← absolute value (not %)
        stackData[`${sk}_abs`] = val
        total += val
      })

      let cumulative = 0

      stacks.forEach(sk => {
        cumulative += stackData[sk]
        stackData[`${sk}_pct`] = total > 0 ? (stackData[sk] / total) * 100 : 0
        stackData[`${sk}_cum`] = cumulative
      })

      stackData[xKey] = xv
      if (total > maxTotal) maxTotal = total
      return stackData
    })

    return { fv, chartData }
  })

  // Round the y-axis max up to a nice number with ~10% headroom for labels
  let yMax = 100
  if (maxTotal > 0) {
    const yMaxRaw = maxTotal * 1.1
    const magnitude = Math.pow(10, Math.floor(Math.log10(yMaxRaw)))
    yMax = Math.ceil(yMaxRaw / magnitude) * magnitude
  }

  return (
    <div
      style={{
        display:"flex",
        gap:6,
        overflowX:"auto",
        paddingBottom:4,
        overflowY:"visible",
      }}
    >
      {allFacetData.map(({ fv, chartData }, fvIdx) => (
        <FacetPanel
          key={fv}
          fv={fv}
          fvIdx={fvIdx}
          chartData={chartData}
          stacks={stacks}
          xKey={xKey}
          facetKey={facetKey}
          colorMap={colorMap}
          h={h}
          yMax={yMax}
        />
      ))}
    </div>
  )
}

const NestedDonut = ({ rowsInner, rowsOuter, makerInner, makerOuter, subColorMap, maxHeight = 460 }) => {
  const aggByProdSub = (rows) => {
    const map = {}

    rows.forEach(r => {
      const sub = r.Sub || "Unknown"
      const prod = r.Product || "Unknown"
      const key = `${prod}||${sub}`

      if (!map[key]) {
        map[key] = {
          product: prod,
          sub,
          value: 0,
        }
      }

      map[key].value += r.Volume
    })

    return Object.values(map)
      .map(d => ({
        name: `${d.product} · ${d.sub}`,
        product: d.product,
        sub: d.sub,
        value: d.value,
      }))
      .sort((a, b) => {
        if (a.product !== b.product) return a.product.localeCompare(b.product)
        return a.sub.localeCompare(b.sub)
      })
  }

  const innerData = aggByProdSub(rowsInner)
  const outerData = aggByProdSub(rowsOuter)

  const innerTotal = innerData.reduce((s, d) => s + d.value, 0)
  const outerTotal = outerData.reduce((s, d) => s + d.value, 0)

  const RADIAN = Math.PI / 180

  const makeLabel = (total) => ({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
    const pct = total > 0 ? Math.round((value / total) * 100) : 0
    if (pct < 4) return null

    const r = innerRadius + (outerRadius - innerRadius) * 0.5
    const x = cx + r * Math.cos(-midAngle * RADIAN)
    const y = cy + r * Math.sin(-midAngle * RADIAN)

    return (
      <g>
        <text
          x={x}
          y={y - 6}
          fill="#fff"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={11}
          fontWeight={700}
          style={{ pointerEvents:"none" }}
        >
          {value.toLocaleString()}
        </text>

        <text
          x={x}
          y={y + 8}
          fill="#fff"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={9}
          fontWeight={500}
          opacity={0.95}
          style={{ pointerEvents:"none" }}
        >
          ({pct}%)
        </text>
      </g>
    )
  }

  if (innerTotal === 0 && outerTotal === 0) return <EmptyState />

  const productList = sorted(uniq([...innerData, ...outerData].map(d => d.product)))

  const shadeColor = (hex, percent) => {
    const num = parseInt(hex.replace("#", ""), 16)

    let r = (num >> 16) + Math.round(2.55 * percent)
    let g = ((num >> 8) & 0xff) + Math.round(2.55 * percent)
    let b = (num & 0xff) + Math.round(2.55 * percent)

    r = Math.max(0, Math.min(255, r))
    g = Math.max(0, Math.min(255, g))
    b = Math.max(0, Math.min(255, b))

    return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")
  }

  const colorFor = (slice) => {
    const base = subColorMap[slice.sub] || "#aaa"
    const productIdx = productList.indexOf(slice.product)

    if (productIdx <= 0) return base

    return shadeColor(base, -18 * productIdx)
  }

  const available = Math.max(220, maxHeight - 36)
  const outerR = Math.round(available * 0.46)
  const outerInnerR = Math.round(available * 0.34)
  const innerR = Math.round(available * 0.30)
  const innerInnerR = Math.round(available * 0.16)

  return (
    <div>
      <div
        style={{
          display:"flex",
          justifyContent:"center",
          gap:24,
          marginBottom:8,
          fontSize:"13px",
          color:T.textSecondary,
          fontWeight:"600",
        }}
      >
        <span>
          <span
            style={{
              color:T.textTertiary,
              fontWeight:"500",
              marginRight:6,
            }}
          >
            Inner:
          </span>
          <span style={{ color:T.textPrimary, fontWeight:"700" }}>
            {makerInner}
          </span>
        </span>

        <span>
          <span
            style={{
              color:T.textTertiary,
              fontWeight:"500",
              marginRight:6,
            }}
          >
            Outer:
          </span>
          <span style={{ color:T.textPrimary, fontWeight:"700" }}>
            {makerOuter}
          </span>
        </span>
      </div>

      <ResponsiveContainer width="100%" height={maxHeight - 36}>
        <PieChart>
          <Pie
            data={innerData}
            cx="50%"
            cy="50%"
            innerRadius={innerInnerR}
            outerRadius={innerR}
            dataKey="value"
            labelLine={false}
            label={makeLabel(innerTotal)}
            isAnimationActive={false}
          >
            {innerData.map((e, i) => (
              <Cell
                key={`in-${i}`}
                fill={colorFor(e)}
                stroke="#fff"
                strokeWidth={2}
              />
            ))}
          </Pie>

          <Pie
            data={outerData}
            cx="50%"
            cy="50%"
            innerRadius={outerInnerR}
            outerRadius={outerR}
            dataKey="value"
            labelLine={false}
            label={makeLabel(outerTotal)}
            isAnimationActive={false}
          >
            {outerData.map((e, i) => (
              <Cell
                key={`out-${i}`}
                fill={colorFor(e)}
                stroke="#fff"
                strokeWidth={2}
              />
            ))}
          </Pie>

          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload || !payload.length) return null

              const p = payload[0]
              const slice = p.payload

              const inInner = innerData.find(
                d => d.name === slice.name && d.value === slice.value
              )

              const ring = inInner ? "inner" : "outer"
              const ringMaker = ring === "inner" ? makerInner : makerOuter
              const ringTotal = ring === "inner" ? innerTotal : outerTotal
              const pct = ringTotal > 0 ? Math.round((slice.value / ringTotal) * 100) : 0

              return (
                <div
                  style={{
                    background:"#fff",
                    border:`1px solid ${T.borderTertiary}`,
                    borderRadius:6,
                    padding:"10px 14px",
                    fontSize:12,
                    boxShadow:"0 4px 12px rgba(0,0,0,0.12)",
                  }}
                >
                  <div
                    style={{
                      fontSize:10,
                      color: T.textTertiary,
                      textTransform:"uppercase",
                      letterSpacing:"0.06em",
                      marginBottom:4,
                    }}
                  >
                    {ringMaker}
                  </div>

                  <div
                    style={{
                      display:"flex",
                      alignItems:"center",
                      gap:8,
                      marginBottom:4,
                    }}
                  >
                    <span
                      style={{
                        width:10,
                        height:10,
                        borderRadius:2,
                        background: colorFor(slice),
                        display:"inline-block",
                      }}
                    />
                    <strong style={{ color: T.textPrimary }}>
                      {slice.product}
                    </strong>
                    <span style={{ color: T.textTertiary }}>·</span>
                    <span style={{ color: T.textPrimary, fontWeight:600 }}>
                      {slice.sub}
                    </span>
                  </div>

                  <div style={{ color: T.textPrimary, fontWeight:600 }}>
                    {slice.value.toLocaleString()}{" "}
                    <span style={{ color: T.textSecondary, fontWeight:500 }}>
                      ({pct}%)
                    </span>
                  </div>
                </div>
              )
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}

const MakerTables = ({ rowsInner, rowsOuter, makerInner, makerOuter, subColorMap }) => {
  const aggByProdSub = (rows) => {
    const map = {}

    rows.forEach(r => {
      const sub = r.Sub || "Unknown"
      const prod = r.Product || "Unknown"
      const key = `${prod}||${sub}`

      if (!map[key]) {
        map[key] = {
          product: prod,
          sub,
          value: 0,
        }
      }

      map[key].value += r.Volume
    })

    return Object.values(map)
      .map(d => ({
        product: d.product,
        sub: d.sub,
        value: d.value,
      }))
      .sort((a, b) => {
        if (a.product !== b.product) return a.product.localeCompare(b.product)
        return a.sub.localeCompare(b.sub)
      })
  }

  const innerData = aggByProdSub(rowsInner)
  const outerData = aggByProdSub(rowsOuter)

  const innerTotal = innerData.reduce((s, d) => s + d.value, 0)
  const outerTotal = outerData.reduce((s, d) => s + d.value, 0)

  const productList = sorted(uniq([...innerData, ...outerData].map(d => d.product)))

  const shadeColor = (hex, percent) => {
    const num = parseInt(hex.replace("#", ""), 16)

    let r = (num >> 16) + Math.round(2.55 * percent)
    let g = ((num >> 8) & 0xff) + Math.round(2.55 * percent)
    let b = (num & 0xff) + Math.round(2.55 * percent)

    r = Math.max(0, Math.min(255, r))
    g = Math.max(0, Math.min(255, g))
    b = Math.max(0, Math.min(255, b))

    return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")
  }

  const colorFor = ({ product, sub }) => {
    const base = subColorMap[sub] || "#aaa"
    const idx = productList.indexOf(product)

    if (idx <= 0) return base

    return shadeColor(base, -18 * idx)
  }

  return (
    <div style={{ marginTop:14 }}>
      {[
        { label: makerInner, data: innerData, total: innerTotal },
        { label: makerOuter, data: outerData, total: outerTotal },
      ].map(({ label, data, total }) => (
        <div key={label} style={{ marginBottom:14 }}>
          <div
            style={{
              fontSize:"10px",
              fontWeight:"700",
              color:T.textPrimary,
              textTransform:"uppercase",
              letterSpacing:"0.06em",
              marginBottom:6,
            }}
          >
            {label}
          </div>

          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"10px" }}>
            <thead>
              <tr
                style={{
                  borderBottom:`2px solid ${T.borderTertiary}`,
                  background:T.bgSecondary,
                }}
              >
                <th
                  style={{
                    textAlign:"left",
                    padding:"5px 6px",
                    fontWeight:"600",
                    color:T.textSecondary,
                    fontSize:"9px",
                    textTransform:"uppercase",
                  }}
                >
                  Cat.
                </th>

                <th
                  style={{
                    textAlign:"right",
                    padding:"5px 6px",
                    fontWeight:"600",
                    color:T.textSecondary,
                    fontSize:"9px",
                    textTransform:"uppercase",
                  }}
                >
                  Vol
                </th>

                <th
                  style={{
                    textAlign:"right",
                    padding:"5px 6px",
                    fontWeight:"600",
                    color:T.textSecondary,
                    fontSize:"9px",
                    textTransform:"uppercase",
                  }}
                >
                  %
                </th>
              </tr>
            </thead>

            <tbody>
              {data.map((d, i) => {
                const pct = total > 0 ? Math.round((d.value / total) * 100) : 0

                return (
                  <tr
                    key={i}
                    style={{
                      borderBottom:`1px solid ${T.borderTertiary}`,
                      background: i % 2 === 0 ? "transparent" : T.bgSecondary,
                    }}
                  >
                    <td
                      style={{
                        padding:"5px 6px",
                        display:"flex",
                        alignItems:"center",
                        gap:5,
                      }}
                    >
                      <span
                        style={{
                          width:8,
                          height:8,
                          borderRadius:2,
                          background: colorFor(d),
                          flexShrink:0,
                          display:"inline-block",
                        }}
                      />
                      <span style={{ color:T.textPrimary, fontSize:"10px" }}>
                        {d.product} {d.sub}
                      </span>
                    </td>

                    <td
                      style={{
                        padding:"5px 6px",
                        textAlign:"right",
                        color:T.textSecondary,
                        fontFamily:"monospace",
                      }}
                    >
                      {d.value.toLocaleString()}
                    </td>

                    <td
                      style={{
                        padding:"5px 6px",
                        textAlign:"right",
                        color:T.textPrimary,
                        fontWeight:"600",
                      }}
                    >
                      {pct}%
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  )
}

const BlockShell = ({ title, children }) => (
  <div
    style={{
      background:T.bgPrimary,
      border:`1px solid ${T.borderTertiary}`,
      borderRadius:T.radiusLg,
      padding:"20px",
      display:"flex",
      flexDirection:"column",
      boxShadow: T.shadow,
      overflow:"visible",
      height:"100%",
    }}
  >
    <div
      style={{
        fontSize:"13px",
        fontWeight:"700",
        color:T.textPrimary,
        textTransform:"uppercase",
        letterSpacing:"0.08em",
        paddingBottom:14,
        marginBottom:14,
        borderBottom:`1px solid ${T.borderTertiary}`,
        textAlign:"center",
      }}
    >
      {title}
    </div>

    <div
      style={{
        flex:1,
        display:"flex",
        gap:16,
        overflow:"visible",
        flexWrap: "wrap",
      }}
    >
      {children}
    </div>
  </div>
)

const FilterPane = ({ children, wide }) => (
  <div
    style={{
      width: wide ? 240 : 145,
      minWidth: wide ? 240 : 145,
      flexShrink:0,
      overflow:"visible",
      zIndex:10,
      position:"relative",
    }}
  >
    {children}
  </div>
)

const ChartPane = ({ children }) => (
  <div
    style={{
      flex:"1 1 320px",
      minWidth:280,
      display:"flex",
      flexDirection:"column",
      justifyContent:"center",
    }}
  >
    {children}
  </div>
)

const Dashboard = ({ raw, onReset, onUploadNew }) => {
  const PRODUCTS = useMemo(() => sorted(uniq(raw.map(r => r.Product))), [raw])
  const MAKERS = useMemo(() => sorted(uniq(raw.map(r => r.Maker))), [raw])
  const YEARS = useMemo(() => sorted(uniq(raw.map(r => r.CY))), [raw])
  const SEGMENTS = useMemo(() => sorted(uniq(raw.map(r => r.Segment))), [raw])
  const SUBS = useMemo(() => sorted(uniq(raw.map(r => r.Sub || ""))).filter(Boolean), [raw])
  const WEIGHT_RANGE_SUBS = useMemo(() => SUBS.filter(isWeightRange), [SUBS])

  const SEG_COLORS = useMemo(() => buildColorMap(SEGMENTS), [SEGMENTS])
  const SUB_COLORS = useMemo(() => buildColorMap(SUBS), [SUBS])
  const WEIGHT_COLORS = useMemo(() => buildColorMap(WEIGHT_RANGE_SUBS), [WEIGHT_RANGE_SUBS])
  const PRODUCT_COLORS = useMemo(() => buildColorMap(PRODUCTS), [PRODUCTS])

  const [b1Prods, setB1Prods] = useState(PRODUCTS.slice(0, 1))
  const [b1Ranges, setB1Ranges] = useState(WEIGHT_RANGE_SUBS)
  const [b1Segs, setB1Segs] = useState(SEGMENTS)
  const [b1Yrs, setB1Yrs] = useState(YEARS)

  const [b2Prods, setB2Prods] = useState(PRODUCTS.slice(0, 1))
  const [b2Subs, setB2Subs] = useState(SUBS)
  const [b2Segs, setB2Segs] = useState(SEGMENTS)
  const [b2Yrs, setB2Yrs] = useState(YEARS)

  const [b3Prods, setB3Prods] = useState(PRODUCTS.slice(0, 1))
  const [b3Segs, setB3Segs] = useState(SEGMENTS)
  const [b3Subs, setB3Subs] = useState(SUBS)
  const [b3Yrs, setB3Yrs] = useState(YEARS)

  const [b4Prods, setB4Prods] = useState(PRODUCTS.slice(0, 1))
  const [b4Segs, setB4Segs] = useState(SEGMENTS)
  const [b4Subs, setB4Subs] = useState(SUBS)
  const [b4Yrs, setB4Yrs] = useState(YEARS)
  const [maker1, setMaker1] = useState(MAKERS[0] || "")
  const [maker2, setMaker2] = useState(MAKERS[1] || "")

  const [showTable, setShowTable] = useState(false)

  // ─── Per-dashboard available options based on currently selected products ───
  // Selecting a product hides sub-segments / segments that don't apply
  // (e.g. picking "PNC-HEC" hides "10.1-15 m", a Telehandlers sub-segment).
  const subsForProds = useCallback(
    (prods) =>
      sorted(uniq(raw.filter(r => prods.includes(r.Product)).map(r => r.Sub || "")))
        .filter(Boolean),
    [raw]
  )
  const segsForProds = useCallback(
    (prods) => sorted(uniq(raw.filter(r => prods.includes(r.Product)).map(r => r.Segment))),
    [raw]
  )

  const availSubs1 = useMemo(() => subsForProds(b1Prods), [subsForProds, b1Prods])
  const availSegs1 = useMemo(() => segsForProds(b1Prods), [segsForProds, b1Prods])
  const availRanges1 = useMemo(() => availSubs1.filter(isWeightRange), [availSubs1])

  const availSubs2 = useMemo(() => subsForProds(b2Prods), [subsForProds, b2Prods])
  const availSegs2 = useMemo(() => segsForProds(b2Prods), [segsForProds, b2Prods])

  const availSubs3 = useMemo(() => subsForProds(b3Prods), [subsForProds, b3Prods])
  const availSegs3 = useMemo(() => segsForProds(b3Prods), [segsForProds, b3Prods])

  const availSubs4 = useMemo(() => subsForProds(b4Prods), [subsForProds, b4Prods])
  const availSegs4 = useMemo(() => segsForProds(b4Prods), [segsForProds, b4Prods])

  // When the available option set changes (because product selection changed),
  // prune the current selections so they stay valid. If everything would
  // disappear, default to all of the new available set.
  useEffect(() => {
    setB1Ranges(prev => {
      const next = prev.filter(v => availRanges1.includes(v))
      return next.length === 0 ? availRanges1 : next
    })
    setB1Segs(prev => {
      const next = prev.filter(v => availSegs1.includes(v))
      return next.length === 0 ? availSegs1 : next
    })
  }, [availRanges1, availSegs1])

  useEffect(() => {
    setB2Subs(prev => {
      const next = prev.filter(v => availSubs2.includes(v))
      return next.length === 0 ? availSubs2 : next
    })
    setB2Segs(prev => {
      const next = prev.filter(v => availSegs2.includes(v))
      return next.length === 0 ? availSegs2 : next
    })
  }, [availSubs2, availSegs2])

  useEffect(() => {
    setB3Subs(prev => {
      const next = prev.filter(v => availSubs3.includes(v))
      return next.length === 0 ? availSubs3 : next
    })
    setB3Segs(prev => {
      const next = prev.filter(v => availSegs3.includes(v))
      return next.length === 0 ? availSegs3 : next
    })
  }, [availSubs3, availSegs3])

  useEffect(() => {
    setB4Subs(prev => {
      const next = prev.filter(v => availSubs4.includes(v))
      return next.length === 0 ? availSubs4 : next
    })
    setB4Segs(prev => {
      const next = prev.filter(v => availSegs4.includes(v))
      return next.length === 0 ? availSegs4 : next
    })
  }, [availSubs4, availSegs4])

  const f1 = useMemo(
    () =>
      raw.filter(
        r =>
          b1Prods.includes(r.Product) &&
          isWeightRange(r.Sub || "") &&
          b1Ranges.includes(r.Sub || "") &&
          b1Segs.includes(r.Segment) &&
          b1Yrs.includes(r.CY)
      ),
    [raw, b1Prods, b1Ranges, b1Segs, b1Yrs]
  )

  const f2 = useMemo(
    () =>
      raw.filter(
        r =>
          b2Prods.includes(r.Product) &&
          b2Subs.includes(r.Sub || "") &&
          b2Segs.includes(r.Segment) &&
          b2Yrs.includes(r.CY)
      ),
    [raw, b2Prods, b2Subs, b2Segs, b2Yrs]
  )

  const f3 = useMemo(
    () =>
      raw.filter(
        r =>
          b3Prods.includes(r.Product) &&
          b3Segs.includes(r.Segment) &&
          b3Subs.includes(r.Sub || "") &&
          b3Yrs.includes(r.CY)
      ),
    [raw, b3Prods, b3Segs, b3Subs, b3Yrs]
  )

  const f4 = useMemo(
    () =>
      raw.filter(
        r =>
          b4Prods.includes(r.Product) &&
          b4Segs.includes(r.Segment) &&
          b4Subs.includes(r.Sub || "") &&
          b4Yrs.includes(r.CY)
      ),
    [raw, b4Prods, b4Segs, b4Subs, b4Yrs]
  )

  const top5_2 = useMemo(() => topNMakers(f2), [f2])
  const top5_3 = useMemo(() => topNMakers(f3), [f3])

  const cd2 = useMemo(() => f2.filter(r => top5_2.includes(r.Maker)), [f2, top5_2])
  const cd3 = useMemo(() => f3.filter(r => top5_3.includes(r.Maker)), [f3, top5_3])

  const df1 = useMemo(() => f4.filter(r => r.Maker === maker1), [f4, maker1])
  const df2 = useMemo(() => f4.filter(r => r.Maker === maker2), [f4, maker2])

  const tableRowCount = useMemo(() => {
    const inner = uniq(df1.map(r => `${r.Product}||${r.Sub || "Unknown"}`)).length
    const outer = uniq(df2.map(r => `${r.Product}||${r.Sub || "Unknown"}`)).length
    return inner + outer
  }, [df1, df2])

  const vp = useViewport()
  const isMobile = vp === "mobile"
  const isTablet = vp === "tablet"
  const isLaptop = vp === "laptop"

  const gridCols = isMobile || isTablet ? "1fr" : "1fr 1fr"

  const chartHBase = isMobile ? 320 : isTablet ? 360 : isLaptop ? 380 : 420
  const donutHBase = isMobile ? 340 : isTablet ? 380 : isLaptop ? 400 : 440

  const tableEstHeight = showTable ? tableRowCount * 22 + 90 : 0
  const extraNeeded = showTable ? Math.max(0, tableEstHeight - 60) : 0

  const donutH = donutHBase + extraNeeded
  // Bottom-left chart (Year-wise competitor) grows alongside donut so the row stays balanced
  const chartHBottom = chartHBase + extraNeeded
  const chartH = chartHBase

  const pagePad = isMobile ? "16px 12px" : isTablet ? "20px 18px" : "32px 28px"
  const titleSize = isMobile ? "22px" : isTablet ? "26px" : "32px"
  const subtitleSize = isMobile ? "13px" : isTablet ? "15px" : "17px"
  const gridGap = isMobile ? 14 : 20

  return (
    <div
      style={{
        padding: pagePad,
        fontFamily:T.font,
        background:"linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
        minHeight:"100vh",
      }}
    >
      <div
        style={{
          display:"grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr auto 1fr",
          alignItems:"center",
          marginBottom: isMobile ? 16 : 24,
          paddingBottom: isMobile ? 14 : 24,
          borderBottom:`2px solid ${T.borderTertiary}`,
          gap:16,
        }}
      >
        {!isMobile && <div />}

        <div style={{ textAlign:"center" }}>
          <div
            style={{
              fontSize: titleSize,
              fontWeight:"800",
              color:T.textPrimary,
              letterSpacing:"-0.025em",
              marginBottom:8,
            }}
          >
            Construction Equipment Dashboard
          </div>

          <div
            style={{
              fontSize: subtitleSize,
              fontWeight:"700",
              color:T.textPrimary,
              letterSpacing:"-0.01em",
              lineHeight: 1.5,
            }}
          >
            {MAKERS.map((m, i) => (
              <span key={m}>
                {i > 0 && (
                  <span
                    style={{
                      color: T.textTertiary,
                      fontWeight:"600",
                      margin:"0 10px",
                    }}
                  >
                    vs
                  </span>
                )}
                <span style={{ color: T.textPrimary, fontWeight:"800" }}>
                  {m}
                </span>
              </span>
            ))}

            <span
              style={{
                color: T.textTertiary,
                fontWeight:"600",
                margin:"0 12px",
              }}
            >
              —
            </span>

            <span style={{ color: T.textPrimary }}>Market Volume Analysis</span>

            <span
              style={{
                color: T.textSecondary,
                fontWeight:"600",
                marginLeft:10,
              }}
            >
              CY {YEARS[0]}
              {YEARS.length > 1 ? `–${YEARS[YEARS.length - 1]}` : ""}
            </span>
          </div>
        </div>

        <div
          style={{
            display:"flex",
            justifyContent: isMobile ? "center" : "flex-end",
            marginTop: isMobile ? 12 : 0,
            gap:8,
            flexWrap:"wrap",
          }}
        >
          <button
            onClick={onReset}
            style={{
              fontSize:"12px",
              padding:"8px 16px",
              cursor:"pointer",
              borderRadius:T.radiusMd,
              border:`1px solid ${T.borderSecondary}`,
              background:T.bgSecondary,
              color:T.textSecondary,
              fontWeight:"500",
            }}
          >
            ↻ Change Makers
          </button>

          <button
            onClick={onUploadNew}
            style={{
              fontSize:"12px",
              padding:"8px 16px",
              cursor:"pointer",
              borderRadius:T.radiusMd,
              border:"1px solid #3b82f6",
              background:"#eff6ff",
              color:"#2563eb",
              fontWeight:"600",
            }}
          >
            ⬆ Upload New Excel
          </button>
        </div>
      </div>

      {/* ─── ROW 1 ─── */}
      <div
        style={{
          display:"grid",
          gridTemplateColumns: gridCols,
          gap: gridGap,
          marginBottom: gridGap,
          alignItems:"stretch",
        }}
      >
        {/* TOP-LEFT (swapped from bottom-left): Segment trend by sub-segment */}
        <BlockShell title="Segment trend (by sub-segment)">
          <FilterPane>
            <MultiSelect label="Product" options={PRODUCTS} value={b1Prods} onChange={setB1Prods} />
            <MultiSelect label="Sub-Segment" options={availRanges1} value={b1Ranges} onChange={setB1Ranges} />
            <MultiSelect label="Segment" options={availSegs1} value={b1Segs} onChange={setB1Segs} />
            <MultiSelect label="Year" options={YEARS} value={b1Yrs} onChange={setB1Yrs} />
            <ChartLegend
              items={sorted(uniq(f1.map(r => r.Sub).filter(Boolean)))}
              colorMap={WEIGHT_COLORS}
              title="Sub-Segments"
            />
          </FilterPane>

          <ChartPane>
            {f1.length === 0 ? (
              <EmptyState />
            ) : (
              <FacetedBar
                data={agg(f1, ["CY", "Product", "Sub"])}
                xKey="CY"
                stackKey="Sub"
                colorMap={WEIGHT_COLORS}
                facetKey="Product"
                facetValues={PRODUCTS.filter(p => b1Prods.includes(p))}
                h={chartH}
              />
            )}
          </ChartPane>
        </BlockShell>

        {/* TOP-RIGHT (unchanged): Competitor — year · product · sub-segment */}
        <BlockShell title="Competitor — year · product · sub-segment">
          <FilterPane>
            <MultiSelect label="Product" options={PRODUCTS} value={b2Prods} onChange={setB2Prods} />
            <MultiSelect label="Sub-Segment" options={availSubs2} value={b2Subs} onChange={setB2Subs} />
            <MultiSelect label="Segment" options={availSegs2} value={b2Segs} onChange={setB2Segs} />
            <MultiSelect label="Year" options={YEARS} value={b2Yrs} onChange={setB2Yrs} />
            <ChartLegend
              items={sorted(uniq(cd2.map(r => r.Sub).filter(Boolean)))}
              colorMap={SUB_COLORS}
              title="Sub-Segments"
            />
          </FilterPane>

          <ChartPane>
            {cd2.length === 0 ? (
              <EmptyState />
            ) : (
              <FacetedBar
                data={agg(cd2, ["CY", "Maker", "Sub"])}
                xKey="Maker"
                stackKey="Sub"
                colorMap={SUB_COLORS}
                facetKey="CY"
                facetValues={YEARS.filter(y => b2Yrs.includes(y))}
                h={chartH}
              />
            )}
          </ChartPane>
        </BlockShell>
      </div>

      {/* ─── ROW 2 ─── */}
      <div
        style={{
          display:"grid",
          gridTemplateColumns: gridCols,
          gap: gridGap,
          alignItems:"stretch",
        }}
      >
        {/* BOTTOM-LEFT (swapped from top-left): Year-wise competitor — segment */}
        <BlockShell title="Year-wise competitor — segment">
          <FilterPane>
            <MultiSelect label="Product" options={PRODUCTS} value={b3Prods} onChange={setB3Prods} />
            <MultiSelect label="Year" options={YEARS} value={b3Yrs} onChange={setB3Yrs} />
            <MultiSelect label="Segment" options={availSegs3} value={b3Segs} onChange={setB3Segs} />
            <MultiSelect label="Sub-Segment" options={availSubs3} value={b3Subs} onChange={setB3Subs} />
            <ChartLegend
              items={sorted(uniq(cd3.map(r => r.Segment)))}
              colorMap={SEG_COLORS}
              title="Segments"
            />
          </FilterPane>

          <ChartPane>
            {cd3.length === 0 ? (
              <EmptyState />
            ) : (
              <FacetedBar
                data={agg(cd3, ["CY", "Maker", "Segment"])}
                xKey="Maker"
                stackKey="Segment"
                colorMap={SEG_COLORS}
                facetKey="CY"
                facetValues={YEARS.filter(y => b3Yrs.includes(y))}
                h={chartHBottom}
              />
            )}
          </ChartPane>
        </BlockShell>

        {/* BOTTOM-RIGHT (unchanged): Sub-segment distribution — maker comparison */}
        <BlockShell title="Sub-segment distribution — maker comparison">
          <FilterPane wide={showTable}>
            <MultiSelect label="Product" options={PRODUCTS} value={b4Prods} onChange={setB4Prods} />
            <MultiSelect label="Segment" options={availSegs4} value={b4Segs} onChange={setB4Segs} />
            <MultiSelect label="Sub-Segment" options={availSubs4} value={b4Subs} onChange={setB4Subs} />
            <MultiSelect label="Year" options={YEARS} value={b4Yrs} onChange={setB4Yrs} />
            <MakerSelect label="Maker 1" value={maker1} onChange={setMaker1} makers={MAKERS} />
            <MakerSelect label="Maker 2" value={maker2} onChange={setMaker2} makers={MAKERS} />

            <div style={{ marginTop:16 }}>
              <div
                style={{
                  fontSize:10,
                  fontWeight:500,
                  color:T.textSecondary,
                  textTransform:"uppercase",
                  letterSpacing:"0.08em",
                  marginBottom:6,
                }}
              >
                Show Table
              </div>

              <button
                onClick={() => setShowTable(v => !v)}
                style={{
                  width:"100%",
                  padding:"6px 8px",
                  cursor:"pointer",
                  borderRadius: T.radiusMd,
                  fontSize:"11px",
                  fontWeight:"600",
                  border:`1px solid ${showTable ? "#3b82f6" : T.borderSecondary}`,
                  background: showTable ? "#eff6ff" : T.bgSecondary,
                  color: showTable ? "#3b82f6" : T.textSecondary,
                }}
              >
                {showTable ? "✓ On" : "Off"}
              </button>
            </div>

            {(() => {
              const productList = sorted(uniq([...df1, ...df2].map(r => r.Product)))

              const allCombos = sorted(
                uniq([...df1, ...df2].map(r => `${r.Product}||${r.Sub || "Unknown"}`))
              ).map(key => {
                const [product, sub] = key.split("||")
                return { product, sub }
              })

              const shadeColor = (hex, percent) => {
                const num = parseInt(hex.replace("#", ""), 16)

                let r = (num >> 16) + Math.round(2.55 * percent)
                let g = ((num >> 8) & 0xff) + Math.round(2.55 * percent)
                let b = (num & 0xff) + Math.round(2.55 * percent)

                r = Math.max(0, Math.min(255, r))
                g = Math.max(0, Math.min(255, g))
                b = Math.max(0, Math.min(255, b))

                return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")
              }

              const colorFor = ({ product, sub }) => {
                const base = SUB_COLORS[sub] || "#aaa"
                const idx = productList.indexOf(product)

                if (idx <= 0) return base

                return shadeColor(base, -18 * idx)
              }

              return (
                <div
                  style={{
                    marginTop:14,
                    padding:"10px 12px",
                    background:T.bgSecondary,
                    borderRadius:T.radiusMd,
                    border:`1px solid ${T.borderTertiary}`,
                  }}
                >
                  <div
                    style={{
                      fontSize:"10px",
                      fontWeight:"600",
                      color:T.textTertiary,
                      textTransform:"uppercase",
                      letterSpacing:"0.07em",
                      marginBottom:8,
                    }}
                  >
                    Legend
                  </div>

                  <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                    {allCombos.map(({ product, sub }) => (
                      <span
                        key={`${product}||${sub}`}
                        style={{
                          display:"flex",
                          alignItems:"center",
                          gap:7,
                          fontSize:"11px",
                          color:T.textPrimary,
                          fontWeight:"500",
                          whiteSpace:"nowrap",
                          overflow:"hidden",
                          textOverflow:"ellipsis",
                        }}
                      >
                        <span
                          style={{
                            width:11,
                            height:11,
                            borderRadius:3,
                            background: colorFor({ product, sub }),
                            display:"inline-block",
                            boxShadow:"0 1px 2px rgba(0,0,0,0.08)",
                            flexShrink:0,
                          }}
                        />
                        <span style={{ overflow:"hidden", textOverflow:"ellipsis" }}>
                          {product} {sub}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              )
            })()}

            {showTable && (
              <MakerTables
                rowsInner={df1}
                rowsOuter={df2}
                makerInner={maker1}
                makerOuter={maker2}
                subColorMap={SUB_COLORS}
              />
            )}
          </FilterPane>

          <ChartPane>
            <NestedDonut
              rowsInner={df1}
              rowsOuter={df2}
              makerInner={maker1}
              makerOuter={maker2}
              subColorMap={SUB_COLORS}
              productColorMap={PRODUCT_COLORS}
              maxHeight={donutH}
            />
          </ChartPane>
        </BlockShell>
      </div>
    </div>
  )
}

const UploadScreen = ({ onData }) => {
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  const handleFile = (file) => {
    if (!file) return

    const validExtensions = [".xlsx", ".xls"]
    const fileName = file.name.toLowerCase()
    const isValid = validExtensions.some(ext => fileName.endsWith(ext))

    if (!isValid) {
      setError("Please upload a valid Excel file: .xlsx or .xls")
      return
    }

    setLoading(true)
    setError("")

    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" })

        const sheetName = wb.SheetNames.includes("CY2022_2025 - Data")
          ? "CY2022_2025 - Data"
          : wb.SheetNames[0]

        const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName])

        if (!rows.length) {
          throw new Error("Sheet appears to be empty.")
        }

        const allKeys = Object.keys(rows[0])

        const findCol = (...candidates) =>
          allKeys.find(k =>
            candidates
              .map(c => c.toLowerCase())
              .includes(k.toLowerCase())
          ) || ""

        const colCY = findCol("CY", "Year", "Calendar Year")
        const colProduct = findCol("Product")
        const colSegment = findCol("Segment")
        const colSub = findCol(
          "Sub-Segment",
          "Sub_Segment",
          "SubSegment",
          "Sub Segment",
          "Subsegment"
        )
        const colMaker = findCol("Maker", "OEM", "Brand")
        const colVolume = findCol("Volume", "Vol", "Units", "Sales")

        const missing = [
          !colCY && "CY/Year",
          !colProduct && "Product",
          !colSegment && "Segment",
          !colMaker && "Maker",
          !colVolume && "Volume",
        ].filter(Boolean)

        if (missing.length) {
          throw new Error(`Could not find columns: ${missing.join(", ")}`)
        }

        const parsed = rows
          .map(r => ({
            CY: Number(r[colCY]),
            Product: String(r[colProduct] ?? "").trim(),
            Segment: String(r[colSegment] ?? "").trim(),
            Sub: colSub ? String(r[colSub] ?? "").trim() : "",
            Maker: String(r[colMaker] ?? "").trim(),
            Volume: Number(r[colVolume] ?? 0),
          }))
          .filter(
            r =>
              r.CY &&
              r.Product &&
              r.Segment &&
              r.Maker &&
              !isNaN(r.Volume)
          )

        if (!parsed.length) {
          throw new Error("No valid rows found.")
        }

        onData(parsed)
      } catch (err) {
        setError(err.message || "Failed to parse Excel file.")
      } finally {
        setLoading(false)
      }
    }

    reader.onerror = () => {
      setError("Failed to read file.")
      setLoading(false)
    }

    reader.readAsArrayBuffer(file)
  }

  return (
    <div
      style={{
        minHeight:"100vh",
        display:"flex",
        alignItems:"center",
        justifyContent:"center",
        background:"linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
        fontFamily:T.font,
        padding:"20px",
      }}
    >
      <div style={{ width:"100%", maxWidth:500, textAlign:"center" }}>
        <div
          style={{
            fontSize:"32px",
            fontWeight:"700",
            color:T.textPrimary,
            marginBottom:8,
          }}
        >
          Construction Equipment
        </div>

        <div
          style={{
            fontSize:"16px",
            fontWeight:"600",
            color:T.textTertiary,
            marginBottom:32,
          }}
        >
          Dashboard
        </div>

        <label
          onDrop={e => {
            e.preventDefault()
            handleFile(e.dataTransfer.files[0])
          }}
          onDragOver={e => e.preventDefault()}
          style={{
            display:"block",
            border:`2px dashed ${T.borderSecondary}`,
            borderRadius:T.radiusLg,
            padding:"48px 32px",
            cursor: loading ? "wait" : "pointer",
            background:T.bgPrimary,
            boxShadow: T.shadow,
            opacity: loading ? 0.75 : 1,
          }}
        >
          <div style={{ fontSize:48, marginBottom:16 }}>📊</div>

          <div
            style={{
              fontSize:"16px",
              fontWeight:"600",
              color:T.textPrimary,
              marginBottom:8,
            }}
          >
            {loading ? "Reading file…" : "Drop your Excel file"}
          </div>

          <div
            style={{
              fontSize:"13px",
              color:T.textTertiary,
              marginBottom:20,
            }}
          >
            or click to browse — .xlsx or .xls files
          </div>

          <div
            style={{
              display:"inline-block",
              padding:"10px 24px",
              background:"#3b82f6",
              color:"#fff",
              borderRadius:T.radiusMd,
              fontSize:"13px",
              fontWeight:"600",
            }}
          >
            Choose File
          </div>

          <input
            type="file"
            accept=".xlsx,.xls"
            disabled={loading}
            onChange={e => handleFile(e.target.files[0])}
            style={{ display:"none" }}
          />
        </label>

        <div
          style={{
            marginTop:16,
            fontSize:"11px",
            color:T.textTertiary,
            lineHeight:1.6,
          }}
        >
          Your Excel file is processed locally in your browser.
          It is not uploaded to AWS or ArcGIS by this app.
        </div>

        {error && (
          <div
            style={{
              marginTop:20,
              padding:"12px 16px",
              background:"#fee2e2",
              border:"1px solid #fca5a5",
              borderRadius:T.radiusMd,
              fontSize:"12px",
              color:"#991b1b",
            }}
          >
            <strong>⚠️ Error:</strong> {error}
          </div>
        )}
      </div>
    </div>
  )
}

const MakerSelectionScreen = ({ raw, onConfirm, onBack }) => {
  const allMakers = useMemo(() => {
    const totals = {}

    raw.forEach(r => {
      totals[r.Maker] = (totals[r.Maker] || 0) + r.Volume
    })

    return Object.entries(totals)
      .sort((a, b) => b[1] - a[1])
      .map(([m, v]) => ({
        maker: m,
        volume: v,
      }))
  }, [raw])

  const [selected, setSelected] = useState(() =>
    allMakers.slice(0, 5).map(m => m.maker)
  )

  const toggle = (maker) => {
    setSelected(prev =>
      prev.includes(maker)
        ? prev.filter(m => m !== maker)
        : [...prev, maker]
    )
  }

  const selectAll = () => setSelected(allMakers.map(m => m.maker))
  const selectNone = () => setSelected([])
  const selectTopN = (n) => setSelected(allMakers.slice(0, n).map(m => m.maker))

  const totalVolume = allMakers.reduce((s, m) => s + m.volume, 0)

  const selectedVolume = allMakers
    .filter(m => selected.includes(m.maker))
    .reduce((s, m) => s + m.volume, 0)

  const coverage = totalVolume > 0
    ? Math.round((selectedVolume / totalVolume) * 100)
    : 0

  return (
    <div
      style={{
        minHeight:"100vh",
        display:"flex",
        alignItems:"center",
        justifyContent:"center",
        background:"linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
        fontFamily:T.font,
        padding:"40px 20px",
      }}
    >
      <div
        style={{
          width:"100%",
          maxWidth:640,
          background:T.bgPrimary,
          borderRadius:T.radiusLg,
          boxShadow:T.shadowLg,
          border:`1px solid ${T.borderTertiary}`,
          overflow:"hidden",
        }}
      >
        <div
          style={{
            padding:"24px 28px",
            borderBottom:`1px solid ${T.borderTertiary}`,
            background:T.bgSecondary,
          }}
        >
          <div
            style={{
              fontSize:"22px",
              fontWeight:"700",
              color:T.textPrimary,
              letterSpacing:"-0.02em",
              marginBottom:4,
            }}
          >
            Select Makers to Compare
          </div>

          <div style={{ fontSize:"13px", color:T.textTertiary }}>
            Choose which makers should be included in your dashboard analysis.
            Found <strong>{allMakers.length}</strong> makers in the data.
          </div>
        </div>

        <div
          style={{
            padding:"16px 28px",
            display:"flex",
            gap:8,
            flexWrap:"wrap",
            alignItems:"center",
            borderBottom:`1px solid ${T.borderTertiary}`,
          }}
        >
          <button
            onClick={selectAll}
            style={{
              fontSize:"11px",
              padding:"6px 12px",
              cursor:"pointer",
              borderRadius:T.radiusMd,
              border:`1px solid ${T.borderSecondary}`,
              background:T.bgSecondary,
              color:T.textSecondary,
              fontWeight:"600",
            }}
          >
            All ({allMakers.length})
          </button>

          <button
            onClick={() => selectTopN(5)}
            style={{
              fontSize:"11px",
              padding:"6px 12px",
              cursor:"pointer",
              borderRadius:T.radiusMd,
              border:`1px solid ${T.borderSecondary}`,
              background:T.bgSecondary,
              color:T.textSecondary,
              fontWeight:"600",
            }}
          >
            Top 5
          </button>

          <button
            onClick={() => selectTopN(10)}
            style={{
              fontSize:"11px",
              padding:"6px 12px",
              cursor:"pointer",
              borderRadius:T.radiusMd,
              border:`1px solid ${T.borderSecondary}`,
              background:T.bgSecondary,
              color:T.textSecondary,
              fontWeight:"600",
            }}
          >
            Top 10
          </button>

          <button
            onClick={selectNone}
            style={{
              fontSize:"11px",
              padding:"6px 12px",
              cursor:"pointer",
              borderRadius:T.radiusMd,
              border:`1px solid ${T.borderSecondary}`,
              background:T.bgSecondary,
              color:"#ef4444",
              fontWeight:"600",
            }}
          >
            None
          </button>

          <div
            style={{
              marginLeft:"auto",
              fontSize:"11px",
              color:T.textTertiary,
              fontWeight:"500",
            }}
          >
            <strong style={{ color:T.textPrimary }}>{selected.length}</strong>{" "}
            / {allMakers.length} selected ·{" "}
            <strong style={{ color:"#3b82f6" }}>{coverage}%</strong> volume coverage
          </div>
        </div>

        <div style={{ maxHeight:380, overflowY:"auto", padding:"8px 0" }}>
          {allMakers.map(({ maker, volume }, i) => {
            const checked = selected.includes(maker)
            const sharePct = totalVolume > 0 ? (volume / totalVolume) * 100 : 0

            return (
              <label
                key={maker}
                onClick={() => toggle(maker)}
                style={{
                  display:"flex",
                  alignItems:"center",
                  gap:12,
                  padding:"10px 28px",
                  cursor:"pointer",
                  background: checked ? "#eff6ff" : "transparent",
                  borderLeft: checked ? "3px solid #3b82f6" : "3px solid transparent",
                  transition:"all 0.15s",
                  userSelect:"none",
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {}}
                  style={{
                    accentColor:"#3b82f6",
                    width:14,
                    height:14,
                    cursor:"pointer",
                    flexShrink:0,
                  }}
                />

                <span
                  style={{
                    fontSize:"11px",
                    color:T.textTertiary,
                    fontWeight:"600",
                    minWidth:24,
                    fontFamily:"monospace",
                  }}
                >
                  #{i + 1}
                </span>

                <span
                  style={{
                    flex:1,
                    fontSize:"13px",
                    fontWeight: checked ? "600" : "500",
                    color: checked ? "#3b82f6" : T.textPrimary,
                  }}
                >
                  {maker}
                </span>

                <span
                  style={{
                    fontSize:"11px",
                    color:T.textSecondary,
                    fontFamily:"monospace",
                    minWidth:80,
                    textAlign:"right",
                  }}
                >
                  {volume.toLocaleString()}
                </span>

                <span
                  style={{
                    fontSize:"11px",
                    fontWeight:"600",
                    color:T.textTertiary,
                    minWidth:48,
                    textAlign:"right",
                  }}
                >
                  {sharePct.toFixed(1)}%
                </span>
              </label>
            )
          })}
        </div>

        <div
          style={{
            padding:"20px 28px",
            borderTop:`1px solid ${T.borderTertiary}`,
            background:T.bgSecondary,
            display:"flex",
            justifyContent:"space-between",
            alignItems:"center",
            gap:12,
          }}
        >
          <button
            onClick={onBack}
            style={{
              fontSize:"12px",
              padding:"10px 18px",
              cursor:"pointer",
              borderRadius:T.radiusMd,
              border:`1px solid ${T.borderSecondary}`,
              background:T.bgPrimary,
              color:T.textSecondary,
              fontWeight:"500",
            }}
          >
            ← Back
          </button>

          <button
            onClick={() => onConfirm(selected)}
            disabled={selected.length === 0}
            style={{
              fontSize:"13px",
              padding:"10px 24px",
              cursor: selected.length === 0 ? "not-allowed" : "pointer",
              borderRadius:T.radiusMd,
              border:"none",
              background: selected.length === 0 ? T.borderSecondary : "#3b82f6",
              color:"#fff",
              fontWeight:"600",
              opacity: selected.length === 0 ? 0.6 : 1,
              transition:"all 0.2s",
            }}
          >
            Continue with {selected.length} maker{selected.length !== 1 ? "s" : ""} →
          </button>
        </div>
      </div>
    </div>
  )
}

export default function App() {
  // Set browser tab title
  useEffect(() => {
    document.title = "EKC Dashboard"
  }, [])

  const [rawAll, setRawAll] = useState(null)
  const [filteredRaw, setFilteredRaw] = useState(null)

  const handleDataLoaded = (data) => {
    setRawAll(data)
    setFilteredRaw(null)
  }

  const handleConfirmMakers = (selectedMakers) => {
    const filtered = rawAll.filter(r => selectedMakers.includes(r.Maker))
    setFilteredRaw(filtered)
  }

  const handleResetToUpload = () => {
    setRawAll(null)
    setFilteredRaw(null)
  }

  const handleResetToMakerSelection = () => {
    setFilteredRaw(null)
  }

  return (
    <>
      <style>{`
        html,
        body,
        #root {
          margin: 0;
          min-height: 100%;
          width: 100%;
        }

        body {
          overflow: auto;
          background: #f8fafc;
        }

        * {
          box-sizing: border-box;
        }

        .recharts-wrapper svg {
          overflow: visible !important;
        }

        .recharts-surface {
          overflow: visible !important;
        }
      `}</style>

      {!rawAll && (
        <UploadScreen onData={handleDataLoaded} />
      )}

      {rawAll && !filteredRaw && (
        <MakerSelectionScreen
          raw={rawAll}
          onConfirm={handleConfirmMakers}
          onBack={handleResetToUpload}
        />
      )}

      {filteredRaw && (
        <Dashboard
          raw={filteredRaw}
          onReset={handleResetToMakerSelection}
          onUploadNew={handleResetToUpload}
        />
      )}
    </>
  )
}
